import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import i18n from "../../config/i18n";
import { sharedApi, getAuthToken } from "../../services/sharedApi";
import { fetchWithCache } from "../../utils/cache/cache";
import { toApiError } from "../../utils/errorUtils";

import type {
  SubscriptionPlan,
  UserSubscription,
  UserLimits,
  SubscriptionState,
} from "../../types/subscription";
import type { AccountSubscription } from "@lingoshade/domain";
import type { PayloadAction } from "@reduxjs/toolkit";

// Параметры кэша для снапшота подписки
const SUBSCRIPTION_CACHE_KEY = "subscription:snapshot";
const SUBSCRIPTION_TTL_MS = 5 * 60 * 1000; // hard TTL
const SUBSCRIPTION_SWR_MS = 2 * 60 * 1000; // окно для stale-while-revalidate

const initialState: SubscriptionState = {
  plans: [],
  currentPlan: null,
  userLimits: null,
  // Новые поля для фичей и использования
  userFeatures: null,
  textUsage: null,
  hasActiveSubscription: false,
  lastFetchedAt: 0,
  loading: false,
  error: null,
};

interface SnapshotPayload {
  plans: SubscriptionPlan[];
  currentPlan: UserSubscription | null;
  userLimits: UserLimits | null;
  userFeatures: Record<string, unknown> | null; // will be narrowed when assigning
  textUsage: unknown; // narrowed to TextUsageInfo
  hasActiveSubscription: boolean;
  fetchedAt: number;
}

// Async thunks
const toCurrentPlan = (
  subscription: AccountSubscription | null,
  plans: SubscriptionPlan[],
): UserSubscription | null => {
  if (!subscription) return null;
  const plan = plans.find((item) => item.id === subscription.planId);
  const planType =
    subscription.plan.name.toLowerCase() === "pro"
      ? "Pro"
      : subscription.plan.name.toLowerCase() === "premium"
        ? "Premium"
        : subscription.plan.name.toLowerCase() === "basic"
          ? "Basic"
          : "Free";
  return {
    id: subscription.id,
    userId: subscription.userId,
    planType,
    monthlyPrice: (plan?.monthlyPrice ?? 0) / 100,
    yearlyPrice: (plan?.yearlyPrice ?? 0) / 100,
    features:
      plan?.features
        .filter((feature) => feature.isEnabled)
        .map((feature) => feature.featureKey) ?? [],
    billingPeriod: subscription.interval === "YEARLY" ? "yearly" : "monthly",
    isRecurring:
      subscription.provider !== "PROMO_CODE" &&
      subscription.status === "ACTIVE",
    startDate: subscription.startDate,
    expiresAt: subscription.endDate ?? undefined,
    status: subscription.status.toLowerCase() as UserSubscription["status"],
    stripeSubscriptionId: subscription.stripeSubscriptionId ?? undefined,
    createdAt: subscription.createdAt,
    updatedAt: subscription.updatedAt,
  };
};

export const fetchSubscriptionPlans = createAsyncThunk<
  SubscriptionPlan[],
  void,
  { rejectValue: string }
>("subscription/fetchPlans", async (_, { rejectWithValue }) => {
  try {
    return await sharedApi.subscriptions.getPlans(getAuthToken());
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error).message);
  }
});

export const fetchCurrentPlan = createAsyncThunk<
  UserSubscription | null,
  void,
  { rejectValue: string }
>("subscription/fetchCurrentPlan", async (_, { rejectWithValue }) => {
  try {
    const token = getAuthToken();
    const [response, plans] = await Promise.all([
      sharedApi.subscriptions.getMySubscription(token),
      sharedApi.subscriptions.getPlans(token),
    ]);
    return toCurrentPlan(response.active, plans);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error).message);
  }
});

export const fetchUserLimits = createAsyncThunk(
  "subscription/fetchUserLimits",
  async (_, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const resp = await sharedApi.subscriptions.getSubscriptionLimits(token);
      if (resp?.success && resp?.data) {
        return resp.data as UserLimits;
      }
      return rejectWithValue(i18n.t("common:errors.noLimitsDataReturned"));
    } catch (error: unknown) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

// Новый объединённый снапшот, чтобы не делать 3 отдельных запроса при каждом заходе на страницу
export const fetchSubscriptionSnapshot = createAsyncThunk<
  SnapshotPayload,
  boolean | undefined,
  { rejectValue: string }
>("subscription/fetchSnapshot", async (force, { rejectWithValue }) => {
  try {
    const payload = await fetchWithCache<SnapshotPayload>({
      key: SUBSCRIPTION_CACHE_KEY,
      ttl: SUBSCRIPTION_TTL_MS,
      swr: SUBSCRIPTION_SWR_MS,
      force: Boolean(force),
      tags: ["subscription"],
      request: async (): Promise<SnapshotPayload> => {
        const token = getAuthToken();
        const [subscriptionInfo, featuresData, plans, limits] =
          await Promise.all([
            sharedApi.subscriptions.getMySubscription(token),
            sharedApi.user.getFeatures(token).catch(() => null),
            sharedApi.subscriptions.getPlans(token),
            sharedApi.subscriptions.getSubscriptionLimits(token),
          ]);
        return {
          plans,
          currentPlan: toCurrentPlan(subscriptionInfo.active, plans),
          userLimits: limits.data,
          userFeatures: featuresData?.features ?? null,
          textUsage: featuresData?.textUsage ?? null,
          hasActiveSubscription: Boolean(
            featuresData?.hasActiveSubscription ||
              subscriptionInfo.active?.status === "ACTIVE" ||
              subscriptionInfo.active?.status === "CANCELLED_AT_PERIOD_END",
          ),
          fetchedAt: Date.now(),
        };
      },
    });
    return payload;
  } catch (error: unknown) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

// Slice
const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentPlan: (state, action: PayloadAction<UserSubscription | null>) => {
      state.currentPlan = action.payload;
    },
    setUserLimits: (state, action: PayloadAction<UserLimits>) => {
      state.userLimits = action.payload;
    },
    resetSubscriptionState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Снапшот
      .addCase(fetchSubscriptionSnapshot.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchSubscriptionSnapshot.fulfilled,
        (state, action: PayloadAction<SnapshotPayload>) => {
          state.loading = false;
          if (!action.payload) return;
          state.plans = action.payload.plans;
          state.currentPlan = action.payload.currentPlan;
          state.userLimits = action.payload.userLimits;
          // Narrow userFeatures
          const uf = action.payload.userFeatures;
          if (uf && typeof uf === "object") {
            const narrowed: Record<string, boolean | number | string> = {};
            Object.entries(uf).forEach(([k, v]) => {
              if (["string", "number", "boolean"].includes(typeof v)) {
                narrowed[k] = v as boolean | number | string;
              }
            });
            state.userFeatures = narrowed;
          } else {
            state.userFeatures = null;
          }
          // Narrow textUsage
          const tu = action.payload.textUsage;
          if (tu && typeof tu === "object") {
            const tx = tu as { [k: string]: unknown };
            state.textUsage = {
              canCreate:
                typeof tx.canCreate === "boolean" ? tx.canCreate : undefined,
              currentCount:
                typeof tx.currentCount === "number"
                  ? tx.currentCount
                  : undefined,
              remainingCount:
                typeof tx.remainingCount === "number"
                  ? tx.remainingCount
                  : undefined,
              limit: typeof tx.limit === "number" ? tx.limit : undefined,
            };
          } else {
            state.textUsage = null;
          }
          state.hasActiveSubscription = action.payload.hasActiveSubscription;
          state.lastFetchedAt = action.payload.fetchedAt;
        },
      )
      .addCase(fetchSubscriptionSnapshot.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch subscription plans
      .addCase(fetchSubscriptionPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchSubscriptionPlans.fulfilled,
        (state, action: PayloadAction<SubscriptionPlan[]>) => {
          state.loading = false;
          state.plans = action.payload;
        },
      )
      .addCase(fetchSubscriptionPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch current plan
      .addCase(fetchCurrentPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchCurrentPlan.fulfilled,
        (state, action: PayloadAction<UserSubscription | null>) => {
          state.loading = false;
          state.currentPlan = action.payload;
        },
      )
      .addCase(fetchCurrentPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch user limits
      .addCase(fetchUserLimits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchUserLimits.fulfilled,
        (state, action: PayloadAction<UserLimits>) => {
          state.loading = false;
          state.userLimits = action.payload;
        },
      )
      .addCase(fetchUserLimits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Actions
export const {
  clearError,
  setCurrentPlan,
  setUserLimits,
  resetSubscriptionState,
} = subscriptionSlice.actions;

// Selectors
export const selectSubscriptionPlans = (state: {
  subscription: SubscriptionState;
}): SubscriptionState["plans"] => state.subscription.plans;
export const selectCurrentPlan = (state: {
  subscription: SubscriptionState;
}): SubscriptionState["currentPlan"] => state.subscription.currentPlan;
export const selectUserLimits = (state: {
  subscription: SubscriptionState;
}): SubscriptionState["userLimits"] => state.subscription.userLimits;
export const selectUserFeatures = (state: {
  subscription: SubscriptionState;
}): SubscriptionState["userFeatures"] => state.subscription.userFeatures;
export const selectTextUsage = (state: {
  subscription: SubscriptionState;
}): SubscriptionState["textUsage"] => state.subscription.textUsage;
export const selectHasActiveSubscription = (state: {
  subscription: SubscriptionState;
}): SubscriptionState["hasActiveSubscription"] =>
  state.subscription.hasActiveSubscription;
export const selectHasAllVoices = (state: {
  subscription: SubscriptionState;
}): boolean =>
  Boolean(
    state.subscription.userLimits?.voices?.hasAllVoices ||
      state.subscription.userFeatures?.["ACCESS_ALL_VOICES"] ||
      state.subscription.hasActiveSubscription,
  );
export const selectSubscriptionLastFetchedAt = (state: {
  subscription: SubscriptionState;
}): SubscriptionState["lastFetchedAt"] => state.subscription.lastFetchedAt;
export const selectSubscriptionLoading = (state: {
  subscription: SubscriptionState;
}): SubscriptionState["loading"] => state.subscription.loading;
export const selectSubscriptionError = (state: {
  subscription: SubscriptionState;
}): SubscriptionState["error"] => state.subscription.error;

export default subscriptionSlice.reducer;
